/**
 * QuickActionsButton Component
 *
 * A unified floating action button (FAB) that expands into a menu
 * with options for AI Helper and Submit Feedback.
 *
 * Replaces the separate ChatWidget FAB and FeedbackButton.
 */

import { useState, useRef, useEffect } from 'react';
import { Plus, X, Bot, MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';
import { useAuth } from '@/contexts/useAuth';
import { cn } from '@/lib/utils';

interface QuickActionsButtonProps {
  /** Whether the chat widget is open (controlled by parent) */
  isChatOpen: boolean;
  /** Callback when chat should open */
  onOpenChat: () => void;
  /** Additional class names */
  className?: string;
}

export function QuickActionsButton({
  isChatOpen,
  onOpenChat,
  className
}: QuickActionsButtonProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  // Close menu on Escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    }

    if (isExpanded) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isExpanded]);

  // Don't render for unauthenticated users or when chat is open
  if (!user || isChatOpen) {
    return null;
  }

  const handleAIHelper = () => {
    setIsExpanded(false);
    onOpenChat();
  };

  const handleFeedback = () => {
    setIsExpanded(false);
    setIsFeedbackOpen(true);
  };

  return (
    <>
      <div
        ref={containerRef}
        className={cn('fixed bottom-6 right-6 z-50 flex flex-row items-center gap-3 pointer-events-none', className)}
      >
        {/* Expandable Menu Items - expand to the left */}
        <div
          className={cn(
            'flex flex-row gap-2 transition-all duration-200 ease-out',
            isExpanded
              ? 'opacity-100 translate-x-0 pointer-events-auto'
              : 'opacity-0 translate-x-4 pointer-events-none'
          )}
        >
          {/* Submit Feedback Option */}
          <button
            onClick={handleFeedback}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-full',
              'bg-background border shadow-lg',
              'hover:bg-accent transition-colors',
              'text-sm font-medium whitespace-nowrap',
              'animate-in slide-in-from-right-2 duration-150'
            )}
            style={{ animationDelay: isExpanded ? '50ms' : '0ms' }}
          >
            <MessageSquarePlus className="h-5 w-5 text-primary" />
            <span>Feedback</span>
          </button>

          {/* AI Helper Option */}
          <button
            onClick={handleAIHelper}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-full',
              'bg-background border shadow-lg',
              'hover:bg-accent transition-colors',
              'text-sm font-medium whitespace-nowrap',
              'animate-in slide-in-from-right-2 duration-150'
            )}
            style={{ animationDelay: isExpanded ? '0ms' : '0ms' }}
          >
            <Bot className="h-5 w-5 text-blue-600" />
            <span>AI Helper</span>
          </button>
        </div>

        {/* Main FAB */}
        <Button
          size="lg"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            'h-14 w-14 rounded-full shadow-lg transition-transform duration-200 pointer-events-auto',
            isExpanded && 'rotate-45'
          )}
          aria-label={isExpanded ? 'Close menu' : 'Open quick actions'}
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <X className="h-6 w-6" />
          ) : (
            <Plus className="h-6 w-6" />
          )}
        </Button>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen} />
    </>
  );
}
